import Container from "@/components/Container/Container";
import Layout from "@/components/Layout/Layout";
import { Heading, H3Css, H5Css } from "@/components/Typography";
import { mq } from "@/constants/breakpoints";
import { css } from "@emotion/react";
import { darkColor } from "@/constants/colors";
import FeatureSectionContentful from "@/components/FeatureSection/FeatureSectionContentful";
import BottomCTA from "@/components/BottomCTA/BottomCTA";
import Testimonials from "@/components/Testimonial/Testimonials";
import { useWindowSize } from "@/hooks/useWindowSize";
import { useMemo } from "react";
import PageFAQSection from "@/components/PageFAQSection";
import FeatureCardWithIcon from "@/components/FeatureSection/FeatureCardWithIcon";
import { contentfulLoader } from "@/components/ContentfulImage/ContentfulImage";
import SmallScreenHero from "./SmallScreenHero";
import LargeScreenHero from "./LargeScreenHero";

export interface WhoWeServePageFields {
  pageTitle?: string;
  metaDescription?: string;
  heroHeading?: string;
  heroSubheading?: string;
  heroImageXs?: any;
  heroImageSm?: any;
  heroImageMd?: any;
  heroImageLg?: any;
  heroImageXl?: any;
  testimonialsHero?: Array<{ fields: any }>;
  testimonials?: Array<{ fields: any }>;
  faqItems?: Array<{ fields: { question: string; answer: string } }>;
  featureSectionHeading?: string;
  featureSectionSubheading?: string;
  featureCards?: Array<{ fields: any }>;
  features?: Array<any>;
}

interface WhoWeServeTemplateProps {
  fields: WhoWeServePageFields;
  heroBackgroundImage: string;
  heroBackgroundIsDark?: boolean;
  featureCardIconStyle?: "enhanced" | "simple";
}

const WhoWeServeTemplate = ({
  fields,
  heroBackgroundImage,
  heroBackgroundIsDark = false,
  featureCardIconStyle = "simple",
}: WhoWeServeTemplateProps) => {
  const { isLg, isXl, isXl2, isXl3 } = useWindowSize();

  const mappedTestimonials = useMemo(
    () => fields?.testimonials?.map((t: any) => t.fields) || [],
    [fields?.testimonials]
  );

  const faqItems = useMemo(
    () =>
      fields?.faqItems?.map((item: any) => ({
        question: item.fields.question,
        answer: item.fields.answer,
      })) || [],
    [fields]
  );

  const featureCardItems = useMemo(
    () => fields?.featureCards?.map((item: any) => item.fields) || [],
    [fields]
  );

  const heroContainerStyles = css`
    background-image: url(${heroBackgroundImage});
    background-size: cover;
    background-repeat: no-repeat;
    position: relative;
    z-index: 1;
  `;

  return (
    <Layout
      title={fields.pageTitle}
      metaDescription={fields.metaDescription}
      heroContainerCss={heroContainerStyles}
      hero={
        <>
          {isLg || isXl || isXl2 || isXl3 ? (
            <LargeScreenHero
              fields={fields}
              contentfulLoader={contentfulLoader}
              heroBackgroundIsDark={heroBackgroundIsDark}
            />
          ) : (
            <SmallScreenHero
              fields={fields}
              contentfulLoader={contentfulLoader}
              heroBackgroundIsDark={heroBackgroundIsDark}
            />
          )}
        </>
      }
    >
      {(fields.featureSectionHeading || fields.featureSectionSubheading) && (
        <Container>
          <div
            css={css`
              margin: 64px 0 0;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            `}
          >
            <>
              {fields.featureSectionHeading && (
                <Heading
                  level={2}
                  css={css`
                    font-size: 10px;
                    font-weight: 500;
                    line-height: 12px;
                    text-align: center;
                    text-transform: uppercase;
                    color: #048cdd;
                    ${mq["lg"]} {
                      font-size: 24px;
                      font-weight: 350;
                      line-height: 31.21px;
                    }
                  `}
                >
                  {fields.featureSectionHeading}
                </Heading>
              )}
              {fields.featureSectionSubheading && (
                <div
                  css={css`
                    text-align: center;
                    margin-top: 24px;
                    ${mq["lg"]} {
                      margin-top: 30px;
                    }
                  `}
                >
                  <Heading
                    level={2}
                    css={css`
                      ${H5Css}
                      color: ${darkColor};
                      ${mq["lg"]} {
                        ${H3Css}
                      }
                    `}
                  >
                    {fields.featureSectionSubheading}
                  </Heading>
                </div>
              )}

              {featureCardItems && featureCardItems.length > 0 && (
                <div
                  css={css`
                    margin-top: 32px;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 32px;
                    width: 100%;
                    justify-content: center;

                    & > * {
                      flex: 0 0 calc(100% - 32px);
                      ${mq["sm"]} {
                        flex: 0 0 calc(50% - 32px);
                      }
                      ${mq["lg"]} {
                        flex: 0 0 calc(33.333% - 32px);
                      }
                    }
                  `}
                >
                  {featureCardItems.map(cardItem => (
                    <FeatureCardWithIcon
                      key={cardItem.featureHeading}
                      cardItem={cardItem}
                      imageSrc={cardItem.cardIcon.fields.file.url}
                      variant="vertical"
                      iconSize={
                        featureCardIconStyle == "enhanced"
                          ? undefined
                          : { width: 36, height: 36 }
                      }
                      useIconContainer={true}
                      iconContainerSize={{ width: 80, height: 80 }}
                    />
                  ))}
                </div>
              )}
            </>
          </div>
        </Container>
      )}

      <Container
        css={css`
          ${!featureCardItems || featureCardItems.length < 1
            ? "margin-top: -106px;"
            : ";"}
        `}
      >
        {(fields?.features ?? []).map((item: any, index: number) => {
          return (
            <FeatureSectionContentful
              key={item.sys.id}
              imageFirst={index % 2 !== 1}
              imageSrc={item.fields.image.fields.file.url}
              sectionHeadline={item.fields.sectionTitle}
              sectionSEOText={item.fields.sectionSEOText}
              feat1Headline={item.fields.item1Title}
              feat1Body={item.fields.item1Description}
              feat2Headline={item.fields.item2Title}
              feat2Body={item.fields.item2Description}
              feat3Headline={item.fields.item3Title}
              feat3Body={item.fields.item3Description}
              feat4Headline={item.fields.item4Title}
              feat4Body={item.fields.item4Description}
            />
          );
        })}
      </Container>
      <Testimonials testimonials={mappedTestimonials} />

      <BottomCTA />

      {/* FAQ Section */}
      <Container id="faq">
        <div
          css={css`
            margin: 96px 0;
          `}
        >
          <PageFAQSection faqItems={faqItems} />
        </div>
      </Container>
    </Layout>
  );
};

export default WhoWeServeTemplate;
